import { useRef, useCallback } from "react";
import { TARGET_SAMPLE_RATE, resampleFloat32 } from "@/utils/pcm";

const captureWorkletUrl = "/pcm-capture-processor.js";

async function ensureWorklet(audioContext: AudioContext, url: string) {
  try {
    await audioContext.audioWorklet.addModule(url);
  } catch (error) {
    const message = error instanceof Error ? error.message : "";
    if (!/already been loaded|already registered/i.test(message)) {
      throw error;
    }
  }
}

export class AudioRecorder {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private sink: MediaStreamAudioDestinationNode | null = null;
  private ownsContext = false;

  constructor(private onAudioData: (audioData: Float32Array) => void) {}

  async start(sharedContext?: AudioContext) {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    if (sharedContext) {
      this.audioContext = sharedContext;
      this.ownsContext = false;
    } else {
      this.audioContext = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
      this.ownsContext = true;
    }

    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    if (!this.audioContext.audioWorklet) {
      throw new Error("AudioWorklet não é suportado neste navegador");
    }

    await ensureWorklet(this.audioContext, captureWorkletUrl);

    this.source = this.audioContext.createMediaStreamSource(this.stream);
    this.worklet = new AudioWorkletNode(this.audioContext, "pcm-capture-processor", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    // Keep the worklet running without sending mic audio to the speakers.
    this.sink = this.audioContext.createMediaStreamDestination();

    this.worklet.port.onmessage = (event) => {
      const samples = event.data as Float32Array;
      if (!samples?.length || !this.audioContext) return;
      const resampled = resampleFloat32(samples, this.audioContext.sampleRate, TARGET_SAMPLE_RATE);
      if (resampled.length) {
        this.onAudioData(resampled);
      }
    };

    this.source.connect(this.worklet);
    this.worklet.connect(this.sink);
  }

  stop() {
    if (this.worklet) {
      this.worklet.port.onmessage = null;
      this.worklet.disconnect();
      this.worklet = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.sink) {
      this.sink.disconnect();
      this.sink = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.ownsContext && this.audioContext) {
      this.audioContext.close();
    }
    this.audioContext = null;
    this.ownsContext = false;
  }
}

export const useAudioCapture = () => {
  const recorderRef = useRef<AudioRecorder | null>(null);

  const startCapture = useCallback(
    async (onAudioData: (data: Float32Array) => void, sharedContext?: AudioContext) => {
      if (recorderRef.current) {
        recorderRef.current.stop();
      }

      recorderRef.current = new AudioRecorder(onAudioData);
      await recorderRef.current.start(sharedContext);
    },
    [],
  );

  const stopCapture = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
  }, []);

  return { startCapture, stopCapture };
};
